import { Ng2StateDeclaration, StatesModule, Transition } from '@uirouter/angular';
import { FiStateNames } from '../shared/fi-state-proxy/fi-state-names';
import { FiOnboardingNavigationTabsComponent } from './fi-onboarding-navigation-tabs/fi-onboarding-navigation-tabs.component';
import { StateAccessCheckService } from '../shared/fi-shared-services/state-access-check.service';
import { AccessRightType, ApplicationSettingsType, ModuleType, ObjectTypes } from '../shared/types';
import { FiOnboardingListComponent } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding-list/fi-onboarding-list.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { FiOnboardingEditComponent } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding-edit/fi-onboarding-edit.component';
import { FiOnboardingService } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding.service';
import { FiOnboardingBuilderHelperService } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding-builder-helper.service';
import { FiOnboardingActiveListComponent } from '@fi-app/fi-onboarding/fi-onboarding-active/fi-onboarding-active-list/fi-onboarding-active-list.component';
import { ISyncOnboardingEditItem } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { FiOnboardingCompletionContainerComponent } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-container/fi-onboarding-completion-container.component';
import { FiOnboardingConnectEditComponent } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding-connect-edit/fi-onboarding-connect-edit.component';
import { AccessRightService } from '@fi-app/shared/fi-shared-services/access-right.service';
import { FiOnboardingCompletionService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion.service';
import { FiOnboardingBuilderStoreService } from './fi-onboarding-builder/fi-onboarding-builder-store.service';
import { IAccessRightFilterCollection } from '@fi-app/shared/models/access-right-filter-collection.model';
import { FiOnboardingGuideComponent } from '@fi-app/fi-onboarding/fi-onboarding-guide/fi-onboarding-guide.component';
import { FiOnboardingViewerComponent } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding-viewer/fi-onboarding-viewer.component';

const OnboardingEdit_highlightedStepElementId = [
    FiStateService,
    Transition,
    FiOnboardingBuilderHelperService,
    FiOnboardingBuilderStoreService,
    (
        fiStateService: FiStateService,
        tr: Transition,
        fiOnboardingBuilderHelperService: FiOnboardingBuilderHelperService,
        fiOnboardingBuilderStoreService: FiOnboardingBuilderStoreService,
    ) => {
        const objectIds = fiStateService.getParams(tr).stepElementObjectIds;
        const objectTypes = fiStateService.getParams(tr).stepElementObjectTypes;
        if (!objectIds?.length || !objectTypes?.length) {
            return;
        }

        const hightlightedStepElementId = fiOnboardingBuilderHelperService.getStepElementId(
            objectIds[0],
            objectTypes[0],
        );
        fiOnboardingBuilderStoreService.setHighlightedStepElementId(hightlightedStepElementId);
    },
];

const OnboardingEdit_accessRightFilterCollection = [
    AccessRightService,
    FiOnboardingBuilderStoreService,
    (accessRightService: AccessRightService, fiOnboardingBuilderStoreService: FiOnboardingBuilderStoreService) => {
        return new Promise<void>((resolve) => {
            accessRightService
                .getAccessRightsForPublicationByObjectType(ObjectTypes.Onboarding)
                .then((accessRights: IAccessRightFilterCollection) => {
                    fiOnboardingBuilderStoreService.setOnboardingAccessRightFilterCollection(accessRights);
                    resolve();
                });
        });
    },
];

const OnboardingEdit_savedQuickSelectionFiltersCollection = [
    AccessRightService,
    FiOnboardingBuilderStoreService,
    (accessRightService: AccessRightService, fiOnboardingBuilderStoreService: FiOnboardingBuilderStoreService) => {
        return new Promise<void>((resolve) => {
            accessRightService.GetSelectionFiltersForPublication(ModuleType.Onboarding, true).then((filters) => {
                fiOnboardingBuilderStoreService.setOnboardingQuickSelectionFilters(filters);
                resolve();
            });
        });
    },
];
const OnboardingEdit_selectedFiltersCollection = [
    Transition,
    FiOnboardingBuilderHelperService,
    (tr: Transition, fiOnboardingBuilderHelperService: FiOnboardingBuilderHelperService) => {
        return Promise.all([
            tr.injector().getAsync('onboarding'),
            tr.injector().getAsync('savedQuickSelectionFilters'),
        ]).then(() => {
            fiOnboardingBuilderHelperService.setInitialSavedQuickSelectionFilters();
        });
    },
];

const Onboarding_stepElementObjectIds = [
    FiStateService,
    Transition,
    (fiStateService: FiStateService, tr: Transition) => fiStateService.getParams(tr).stepElementObjectIds,
];

const Onboarding_stepElementObjectTypes = [
    FiStateService,
    Transition,
    (fiStateService: FiStateService, tr: Transition) => fiStateService.getParams(tr).stepElementObjectTypes,
];

const OnboardingConnectionEdit_syncReadyOnboardings = [
    FiOnboardingService,
    (fiOnboardingService: FiOnboardingService) => fiOnboardingService.getSyncReadyOnboardings(),
];

const OnboardingAdd_onboarding = [
    FiStateService,
    Transition,
    FiOnboardingBuilderHelperService,
    (
        fiStateService: FiStateService,
        tr: Transition,
        fiOnboardingBuilderHelperService: FiOnboardingBuilderHelperService,
    ) => {
        fiOnboardingBuilderHelperService.initStoreDataNewOnboarding(fiStateService.getParams(tr).onboarding);
    },
];

const OnboardingEdit_onboarding = [
    FiOnboardingBuilderHelperService,
    FiStateService,
    Transition,
    (
        fiOnboardingBuilderHelperService: FiOnboardingBuilderHelperService,
        fiStateService: FiStateService,
        tr: Transition,
    ) => {
        const onboardingId: number = coerceNumberProperty(fiStateService.getParams(tr).id);
        return fiOnboardingBuilderHelperService.initStoreDataExistingOnboarding(onboardingId);
    },
];

const OnboardingView_onboarding = [
    FiOnboardingService,
    FiStateService,
    Transition,
    (fiOnboardingService: FiOnboardingService, fiStateService: FiStateService, tr: Transition) => {
        const onboardingId: number = coerceNumberProperty(fiStateService.getParams(tr).id);
        return fiOnboardingService.getOnboardingViewer(onboardingId);
    },
];

const OnboardingCopy_onboarding = [
    FiOnboardingBuilderHelperService,
    FiStateService,
    Transition,
    (
        fiOnboardingBuilderHelperService: FiOnboardingBuilderHelperService,
        fiStateService: FiStateService,
        tr: Transition,
    ) => {
        const onboardingId: number = coerceNumberProperty(fiStateService.getParams(tr).id);
        return fiOnboardingBuilderHelperService.initStoreDataOnboardingCopy(onboardingId);
    },
];

const OnboardingState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding._name,
    url: '/Onboarding',
    abstract: true,
    component: FiOnboardingNavigationTabsComponent,
};

const OnboardingConnectionState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Connection._name,
    url: '/Connection',
    abstract: true,
};

export const OnboardingActiveState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Active,
    url: '/Active?:selectedStatus&:sortColumn&:sortDirection',
    component: FiOnboardingActiveListComponent,
    resolve: {},
    reloadOnSearch: false,
};

export const OnboardingGuideState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Guide,
    url: '/Guide',
    component: FiOnboardingGuideComponent,
};

export const OnboardingListState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.List,
    params: {
        ids: undefined,
        stepElementObjectIds: undefined,
        stepElementObjectTypes: undefined,
    },
    url: '/?:filterType&:sortColumn&:sortDirection&:offset',
    component: FiOnboardingListComponent,
    resolve: {
        onboardingIds: [
            FiStateService,
            Transition,
            (fiStateService: FiStateService, tr: Transition) => fiStateService.getParams(tr).ids,
        ],
        stepElementObjectIds: Onboarding_stepElementObjectIds,
        stepElementObjectTypes: Onboarding_stepElementObjectTypes,
    },
    reloadOnSearch: false,
};

export const OnboardingAddState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Add,
    url: '/Add',
    views: {
        '@Module': {
            component: FiOnboardingEditComponent,
        },
    },
    params: {
        onboarding: null,
    },
    resolve: {
        onboarding: OnboardingAdd_onboarding,
        accessRightFilterCollection: OnboardingEdit_accessRightFilterCollection,
        savedQuickSelectionFilters: OnboardingEdit_savedQuickSelectionFiltersCollection,
    },
};

export const OnboardingConnectionAddState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Connection.Add,
    url: '/Add',
    views: {
        '@Module': {
            component: FiOnboardingConnectEditComponent,
        },
    },
    params: {
        onboarding: null,
    },
    resolve: {
        onboarding: [
            FiStateService,
            Transition,
            FiOnboardingBuilderHelperService,
            (
                fiStateService: FiStateService,
                tr: Transition,
                fiOnboardingBuilderHelperService: FiOnboardingBuilderHelperService,
            ) => {
                const onboarding: ISyncOnboardingEditItem = fiStateService.getParams(tr).onboarding;
                return onboarding ?? fiOnboardingBuilderHelperService.getNewSyncOnboardingInstance();
            },
        ],
        syncReadyOnboardings: OnboardingConnectionEdit_syncReadyOnboardings,
    },
};

export const OnboardingConnectionEditState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Connection.Edit,
    url: '/Edit/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiOnboardingConnectEditComponent,
        },
    },
    params: {
        onboarding: null,
    },
    resolve: {
        onboarding: OnboardingEdit_onboarding,
        syncReadyOnboardings: OnboardingConnectionEdit_syncReadyOnboardings,
    },
};

export const OnboardingEditState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Edit,
    url: '/Edit/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiOnboardingEditComponent,
        },
    },
    params: {
        stepElementObjectIds: undefined,
        stepElementObjectTypes: undefined,
    },
    resolve: {
        highlightedStepElementId: OnboardingEdit_highlightedStepElementId,
        onboarding: OnboardingEdit_onboarding,
        accessRightFilterCollection: OnboardingEdit_accessRightFilterCollection,
        savedQuickSelectionFilters: OnboardingEdit_savedQuickSelectionFiltersCollection,
        selectedFiltersCollection: OnboardingEdit_selectedFiltersCollection,
    },
};


export const OnboardingViewState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.View,
    url: '/View/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiOnboardingViewerComponent,
        },
    },
    params: {
        stepElementObjectIds: undefined,
        stepElementObjectTypes: undefined,
    },
    resolve: {
        onboardingViewer: OnboardingView_onboarding,
        highlightedStepElementId: OnboardingEdit_highlightedStepElementId,
        stepElementObjectIds: Onboarding_stepElementObjectIds,
        stepElementObjectTypes: Onboarding_stepElementObjectTypes,
    },
};

export const OnboardingCopyState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Copy,
    url: '/Copy/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiOnboardingEditComponent,
        },
    },
    resolve: {
        isCopyMode: [() => true],
        onboarding: OnboardingCopy_onboarding,
        accessRightFilterCollection: OnboardingEdit_accessRightFilterCollection,
        savedQuickSelectionFilters: OnboardingEdit_savedQuickSelectionFiltersCollection,
    },
};

export const OnboardingCompletionContainerState: Ng2StateDeclaration = {
    name: FiStateNames.Onboarding.Completion,
    url: '/Completion/{id:[0-9]+}',
    views: {
        '@Module': {
            component: FiOnboardingCompletionContainerComponent,
        },
    },
    resolve: {
        onboardingCompletionData: [
            FiOnboardingCompletionService,
            FiStateService,
            Transition,
            (
                fiOnboardingCompletionService: FiOnboardingCompletionService,
                fiStateService: FiStateService,
                tr: Transition,
            ) => {
                const onboardingId: number = coerceNumberProperty(fiStateService.getParams(tr).id);
                return fiOnboardingCompletionService.getOnboardingCompletionData(onboardingId);
            },
        ],
    },
};

export const OnboardingStates: StatesModule = {
    states: [
        OnboardingState,
        OnboardingConnectionState,
        OnboardingListState,
        OnboardingGuideState,
        OnboardingAddState,
        OnboardingEditState,
        OnboardingViewState,
        OnboardingCopyState,
        OnboardingActiveState,
        OnboardingCompletionContainerState,
        OnboardingConnectionAddState,
        OnboardingConnectionEditState,
    ],
};

export function registerOnboardingStateRules(stateAccessCheckService: StateAccessCheckService) {
    stateAccessCheckService.registerStateRules(
        [
            FiStateNames.Onboarding.Active,
            FiStateNames.Onboarding.List,
            FiStateNames.Onboarding.Todo.List,
            FiStateNames.Onboarding.Quiz.List,
        ],
        {
            SettingType: [ApplicationSettingsType.OnboardingLocal],
            AccessType: AccessRightType.Read,
        },
    );
    stateAccessCheckService.registerStateRules(
        [
            FiStateNames.Onboarding.Add,
            FiStateNames.Onboarding.Edit,
            FiStateNames.Onboarding.View,
            FiStateNames.Onboarding.Todo.Add._name,
            FiStateNames.Onboarding.Todo.Add.Sections._name,
            FiStateNames.Onboarding.Todo.Add.Sections.Item,
            FiStateNames.Onboarding.Todo.Edit._name,
            FiStateNames.Onboarding.Todo.Edit.Sections._name,
            FiStateNames.Onboarding.Todo.Edit.Sections.Item,
            FiStateNames.Onboarding.Todo.View,
            FiStateNames.Onboarding.Quiz.Add,
            FiStateNames.Onboarding.Quiz.Edit,
            FiStateNames.Onboarding.Quiz.View,
        ],
        {
            SettingType: [ApplicationSettingsType.OnboardingLocal],
            AccessType: AccessRightType.Share,
        },
    );
}
